/**
 * <recruitment-form-component>
 * Handles the SERICA recruitment application form: client-side validation,
 * multipart/form-data submission (incl. file attachments) to the external
 * backend defined in data-endpoint, and success/error UI states.
 */
(() => {
  if (customElements.get('recruitment-form-component')) return;

  class RecruitmentFormComponent extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-recruitment-form]');
      if (!this.form) return;

      this.submitButton = this.querySelector('[data-recruitment-submit]');
      this.submitLabel = this.querySelector('[data-recruitment-submit-label]');
      this.successEl = this.querySelector('[data-recruitment-success]');
      this.errorEl = this.querySelector('[data-recruitment-error]');

      this.endpoint = this.dataset.endpoint || '';
      this.maxSizeBytes = (parseFloat(this.dataset.maxSizeMb) || 8) * 1024 * 1024;

      this.messages = {
        success: this.dataset.msgSuccess,
        error: this.dataset.msgError,
        errorRequired: this.dataset.msgErrorRequired,
        errorFileSize: this.dataset.msgErrorFileSize,
        errorFileType: this.dataset.msgErrorFileType,
        errorFileCount: this.dataset.msgErrorFileCount,
        submit: this.dataset.msgSubmit,
        submitting: this.dataset.msgSubmitting,
      };

      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    hideStatus() {
      this.successEl?.setAttribute('hidden', '');
      this.errorEl?.setAttribute('hidden', '');
    }

    showSuccess() {
      this.hideStatus();
      if (!this.successEl) return;
      this.successEl.textContent = this.messages.success;
      this.successEl.removeAttribute('hidden');
      this.successEl.focus();
    }

    showError(message) {
      this.hideStatus();
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.messages.error;
      this.errorEl.removeAttribute('hidden');
      this.errorEl.focus();
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('recruitment-form__submit--loading', isLoading);
      if (this.submitLabel) {
        this.submitLabel.textContent = isLoading ? this.messages.submitting : this.messages.submit;
      }
    }

    validateFiles() {
      const fileInputs = this.form.querySelectorAll('input[type="file"]');
      for (const input of fileInputs) {
        const maxFiles = parseInt(input.dataset.maxFiles, 10);
        if (maxFiles && input.files.length > maxFiles) {
          return { valid: false, message: this.messages.errorFileCount };
        }
        for (const file of input.files) {
          if (file.size > this.maxSizeBytes) {
            return { valid: false, message: `${this.messages.errorFileSize} (${file.name})` };
          }
        }
      }
      return { valid: true };
    }

    async onSubmit(event) {
      event.preventDefault();
      this.hideStatus();

      const honeypot = this.form.querySelector('input[name="website"]');
      if (honeypot && honeypot.value) {
        // Likely a bot: pretend success without actually sending anything.
        this.form.reset();
        this.showSuccess();
        return;
      }

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        this.showError(this.messages.errorRequired);
        return;
      }

      const fileCheck = this.validateFiles();
      if (!fileCheck.valid) {
        this.showError(fileCheck.message);
        return;
      }

      if (!this.endpoint) {
        this.showError(this.messages.error);
        return;
      }

      const formData = new FormData(this.form);
      formData.append('pageUrl', window.location.href);

      this.setLoading(true);

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          body: formData,
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (err) {
          payload = null;
        }

        if (response.ok && payload?.success !== false) {
          this.form.reset();
          this.showSuccess();
        } else {
          this.showError(payload?.message);
        }
      } catch (err) {
        this.showError(this.messages.error);
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('recruitment-form-component', RecruitmentFormComponent);

  // Présélectionne un poste dans le formulaire et y amène le visiteur.
  // Utilisé par : les boutons "Postuler à ce poste" de l'accordéon des
  // offres, et le lien "Postuler" d'une page de fiche de poste individuelle
  // (paramètre d'URL ?poste=...).
  function applyToPosition(position) {
    const select = document.querySelector('[data-recruitment-form] select[name="position"]');
    if (!select) return;

    const optionExists = Array.from(select.options).some((option) => option.value === position);
    if (optionExists) {
      select.value = position;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const formSection = select.closest('.recruitment-form-section');
    formSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-apply-position]');
    if (!trigger) return;
    applyToPosition(trigger.dataset.applyPosition);
  });

  document.addEventListener('DOMContentLoaded', () => {
    const position = new URLSearchParams(window.location.search).get('poste');
    if (position) applyToPosition(position);
  });
})();
